import React, { useState, useEffect } from 'react';
import styles from './FlatFilters.module.css';
import InternalHeader from '../../../InternalHeader/InternalHeader';
import RangeSlider from '../../../UI/RangeSlider/RangeSlider';
import SelectButton from '../../../UI/SelectButton/SelectButton';
import Button from '../../../UI/Button/Button';
import { FlatCardData, OptionData } from '../../../CardsComponents/types';
import { FilterSmallState } from '../../SmallFiltersComponents/Filter/Filter';

export type FilterBigState = {
  type: string[];
  window_options: string[];
  special_options: string[];
  project_options: string[];
  price_range: [number, number];
  floor_range: [number, number];
  area_range?: [number, number];
  projectType: string | null;
};

type FlatFiltersProps = {
  onApplyFilter: (filters: FilterBigState) => void;
  onClose: () => void;
  cardData: FlatCardData[];
  smallFilters: FilterSmallState;
  setSmallFilters: (filters: FilterSmallState) => void;
};

const FlatFilters: React.FC<FlatFiltersProps> = ({
  onApplyFilter,
  onClose,
  cardData,
  smallFilters,
  setSmallFilters,
}) => {
  const [initialPriceRange, setInitialPriceRange] = useState<[number, number]>([0, 0]);
  const [initialFloorRange, setInitialFloorRange] = useState<[number, number]>([0, 0]);
  const [initialAreaRange, setInitialAreaRange] = useState<[number, number]>([0, 0]);

  const initialFilters: FilterBigState = {
    type: [],
    window_options: [],
    special_options: [],
    project_options: [],
    price_range: initialPriceRange,
    floor_range: initialFloorRange,
    area_range: initialAreaRange,
    projectType: null,
  };

  const [filters, setFilters] = useState<FilterBigState>(initialFilters);
  const [priceLimits, setPriceLimits] = useState<[number, number]>([0, 0]);
  const [floorLimits, setFloorLimits] = useState<[number, number]>([0, 0]);
  const [areaLimits, setAreaLimits] = useState<[number, number]>([0, 0]);
  const [filteredCardCount, setFilteredCardCount] = useState(0);

  const calculateDiscountedPrice = (pricePerM2: string | number, discountProperty: number, totalPrice: string | number): string => {
    const pricePerM2Number = parseInt((pricePerM2 || '').toString().replace(/\s+/g, ''), 10);
    const totalPriceNumber = parseInt((totalPrice || '').toString().replace(/\s+/g, ''), 10);

    if (isNaN(pricePerM2Number) || isNaN(totalPriceNumber) || discountProperty <= 0) {
      return totalPrice.toString();
    }

    const area = totalPriceNumber / pricePerM2Number;
    const discountTotal = discountProperty * area;
    const discountedTotalPrice = Math.floor(totalPriceNumber - discountTotal);

    return discountedTotalPrice.toLocaleString('ru-RU');
  };

  useEffect(() => {
    const prices = cardData.map((card) => {
      const discountedPrice = calculateDiscountedPrice(
        card.pricePerM2 ?? '0',
        card.discountProperty ?? 0,
        card.price ?? '0'
      );
      return parseInt(discountedPrice.replace(/\s+/g, ''), 10);
    });

    const floors = cardData.map((card) => card.floor);
    const areas = cardData.map((card) =>
      parseFloat(parseFloat((card.area || '').toString().replace(',', '.')).toFixed(1))
    );

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const minFloor = Math.min(...floors);
    const maxFloor = Math.max(...floors);
    const minArea = Math.min(...areas);
    const maxArea = Math.max(...areas);

    setPriceLimits([minPrice, maxPrice]);
    setFloorLimits([minFloor, maxFloor]);
    setAreaLimits([minArea, maxArea]);

    setInitialPriceRange([minPrice, maxPrice]);
    setInitialFloorRange([minFloor, maxFloor]);
    setInitialAreaRange([minArea, maxArea]);

    setFilters((prev) => ({
      ...prev,
      price_range: [minPrice, maxPrice],
      floor_range: [minFloor, maxFloor],
      area_range: [minArea, maxArea],
    }));
  }, [cardData]);

  const toggleType = (type: string) => {
    const updatedFilters = {
      ...filters,
      type: filters.type.includes(type)
        ? filters.type.filter((t) => t !== type)
        : [...filters.type, type],
    };
    setFilters(updatedFilters);
    setSmallFilters({
      ...smallFilters,
      type: updatedFilters.type,
    });
  };

  const updateRange = (key: keyof FilterBigState, value: [number, number]) => {
    const updatedFilters = {
      ...filters,
      [key]: [parseFloat(value[0].toFixed(1)), parseFloat(value[1].toFixed(1))],
    };
    setFilters(updatedFilters);
    setSmallFilters({
      ...smallFilters,
      [key]: value,
    });
  };

  const toggleWindowOption = (option: string) => {
    setFilters((prev) => ({
      ...prev,
      window_options: prev.window_options.includes(option)
        ? prev.window_options.filter((o) => o !== option)
        : [...prev.window_options, option],
    }));
  };

  const toggleSpecialOption = (option: string) => {
    setFilters((prev) => ({
      ...prev,
      special_options: prev.special_options.includes(option)
        ? prev.special_options.filter((o) => o !== option)
        : [...prev.special_options, option],
    }));
  };

  const resetFilters = () => {
    setFilters({
      ...initialFilters,
      price_range: initialPriceRange,
      floor_range: initialFloorRange,
      area_range: initialAreaRange,
    });
    setSmallFilters({
      type: [],
      price_range: initialPriceRange,
      floor_range: initialFloorRange,
    });
  };

  const handleApplyFilter = () => {
    if (filteredCardCount > 0) {
      onApplyFilter(filters);
      onClose();
    }
  };

  const countActiveFilters = () => {
    let count =
      filters.type.length +
      filters.window_options.length +
      filters.special_options.length;

    if (
      filters.price_range[0] !== initialPriceRange[0] ||
      filters.price_range[1] !== initialPriceRange[1]
    ) {
      count++;
    }
    if (
      filters.floor_range[0] !== initialFloorRange[0] ||
      filters.floor_range[1] !== initialFloorRange[1]
    ) {
      count++;
    }
    if (
      filters.area_range &&
      (filters.area_range[0] !== initialAreaRange[0] ||
        filters.area_range[1] !== initialAreaRange[1])
    ) {
      count++;
    }
    if (filters.projectType !== null) {
      count++;
    }
    return count;
  };

  const flatTypes = ['Студия', '1-комнатная', '2-комнатная', '3-комнатная'];
  const windowViews = [
    'Во двор',
    'В город',
    'На солнечную сторону',
    'На теневую сторону',
  ];
  const specialFeatures = [
    'Горящие предложения',
    'Европланировка',
    'Мастер-спальня',
    'Два санузла',
    'Разделенный санузел',
    'Балкон',
    'Гардероб',
    'Кладовые'
  ];

  useEffect(() => {
    const filteredCards = cardData.filter((card) => {
      const discountedPrice = calculateDiscountedPrice(
        card.pricePerM2 ?? '0',
        card.discountProperty ?? 0,
        card.price ?? '0'
      );
      const price = parseInt(discountedPrice.replace(/\s+/g, ''), 10);
      const floor = card.floor;
      const area = parseFloat((card.area || '').toString().replace(',', '.'));

      const matchesType = !filters.type.length || (card.roomType && filters.type.includes(card.roomType));
      const matchesWindowOptions = !filters.window_options.length || filters.window_options.every((option) => card.windowOptions.some((wo: OptionData) => wo.name === option));
      const matchesSpecialOptions = !filters.special_options.length || filters.special_options.every((option) => card.specialOptions.some((so: OptionData) => so.name === option));
      const matchesPrice = price >= filters.price_range[0] && price <= filters.price_range[1];
      const matchesFloor = floor >= filters.floor_range[0] && floor <= filters.floor_range[1];
      const matchesArea = filters.area_range
        ? area >= filters.area_range[0] - 0.05 && area <= filters.area_range[1] + 0.05
        : true;

      return matchesType && matchesWindowOptions && matchesSpecialOptions && matchesPrice && matchesFloor && matchesArea;
    });

    setFilteredCardCount(filteredCards.length);
  }, [filters, cardData]);

  const getParametersEnding = (count: number): string => {
    if (count % 10 === 1 && count % 100 !== 11) {
      return 'параметр';
    } else if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
      return 'параметра';
    } else {
      return 'параметров';
    }
  };

  const parametersCount = countActiveFilters();
  const parametersEnding = getParametersEnding(parametersCount);

  return (
    <div className={styles.modal}>
      <div className={styles.overlay}>
        <InternalHeader title='Фильтры' onClose={onClose} />
        <div className={styles.body}>
          <div className={styles.content_wrapper}>
            <div className={styles.filters_title}>
              <h3>Фильтры</h3>
            </div>
            <div className={styles.flat_content}>
              <div className={styles.flat_price}>
                <div className={styles.filter_label}>
                  <p className='text_captions_c2'>Стоимость</p>
                </div>
                <RangeSlider
                  min={priceLimits[0]}
                  max={priceLimits[1]}
                  value={smallFilters.price_range}
                  onChange={(value) => updateRange('price_range', value as [number, number])}
                  label='₽'
                  theme='light'
                />
              </div>
              <div className={styles.flat_type}>
                <div className={styles.filter_label}>
                  <p className='text_captions_c2'>Тип квартиры</p>
                </div>
                <div className={styles.filter_select_buttons}>
                  {flatTypes.map((type) => (
                    <SelectButton
                      key={type}
                      label={type}
                      isActive={smallFilters.type.includes(type)}
                      onClick={() => toggleType(type)}
                      theme='light'
                    />
                  ))}
                </div>
              </div>
              <div className={styles.flat_square}>
                <div className={styles.filter_label}>
                  <p className='text_captions_c2'>Площадь</p>
                </div>
                <RangeSlider
                  min={areaLimits[0]}
                  max={areaLimits[1]}
                  value={filters.area_range ?? [0, 0]}
                  onChange={(value) => updateRange('area_range', value as [number, number])}
                  label='м²'
                  step={0.1}
                  decimalPlaces={1}
                  theme='light'
                />
              </div>
              <div className={styles.flat_floor}>
                <div className={styles.filter_label}>
                  <p className='text_captions_c2'>Этаж</p>
                </div>
                <RangeSlider
                  min={floorLimits[0]}
                  max={floorLimits[1]}
                  value={smallFilters.floor_range}
                  onChange={(value) => updateRange('floor_range', value as [number, number])}
                  label='этажа'
                  theme='light'
                />
              </div>
              <div className={styles.flat_window}>
                <div className={styles.filter_label}>
                  <p className='text_captions_c2'>Вид из окна</p>
                </div>
                <div className={styles.filter_select_buttons}>
                  {windowViews.map((view) => (
                    <SelectButton
                      key={view}
                      label={view}
                      isActive={filters.window_options.includes(view)}
                      onClick={() => toggleWindowOption(view)}
                      theme='light'
                    />
                  ))}
                </div>
              </div>
              <div className={styles.flat_special}>
                <div className={styles.filter_label}>
                  <p className='text_captions_c2'>Особенности</p>
                </div>
                <div className={styles.filter_select_buttons}>
                  {specialFeatures.map((feature) => (
                    <SelectButton
                      key={feature}
                      label={feature}
                      isActive={filters.special_options.includes(feature)}
                      onClick={() => toggleSpecialOption(feature)}
                      theme='light'
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.buttons}>
          <Button
            onClick={handleApplyFilter}
            roundType={'small_rounded'}
            colorType={'wb'}
            style={{
              width: '100%',
              backdropFilter: 'blur(100px)',
              background: filteredCardCount > 0 ? 'var(--color-black)' : 'var(--color-black-inactive)',
              pointerEvents: filteredCardCount > 0 ? 'auto' : 'none',
            }}
          >
            Применить {parametersCount > 0 && `${parametersCount} ${parametersEnding}`}
          </Button>
          <Button onClick={resetFilters} roundType={'small_rounded'} colorType={'gray'}>
            Сбросить
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FlatFilters;